import useEmblaCarousel from "embla-carousel-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useWindowSize } from "react-use";
import { DotButton, useDotButton } from "./dot-button";
import Autoplay from "embla-carousel-autoplay";
import AutoHeight from "embla-carousel-auto-height";
import ClassNames from "embla-carousel-class-names";
import FadeReveal from "./plugins/fade-reveal";

const SliderContext = createContext();

export const useSliderContext = () => {
  return useContext(SliderContext);
};

const DEFAULT_OPTIONS = {
  loop: true,
  gap: 0,
  autoplay: false,
  autoplaySpeed: 5000,
  active: true,
  adaptiveHeight: false,
  fade: false,
};

const SliderProvider = ({ children, options, slideTypes = [] }) => {
  const emblaOptions = { ...DEFAULT_OPTIONS, ...options };

  // build plugins list dynamically
  const plugins = [];

  plugins.push(ClassNames());

  if (emblaOptions.autoplay) {
    plugins.push(
      Autoplay({
        playOnInit: true,
        delay: emblaOptions.autoplaySpeed,
        active: emblaOptions.autoplay,
        stopOnInteraction: false,
        stopOnLastSnap: false,
      })
    );
  }

  if (emblaOptions.autoHeight) {
    plugins.push(AutoHeight());
  }

  if (emblaOptions.fade) {
    plugins.push(FadeReveal());
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);
  const { width } = useWindowSize();
  const { scrollSnaps } = useDotButton(emblaApi || null) || { scrollSnaps: [] };

  const containerRef = useRef();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const [prevIndex, setPrevIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(0);

  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  const [slideDurations, setSlideDurationsState] = useState(() =>
    slideTypes.map(() => emblaOptions.autoplaySpeed)
  );

  const setSlideDuration = useCallback((index, ms) => {
    setSlideDurationsState((prev) => {
      const next = [...prev];
      next[index] = ms;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const snaps = emblaApi.scrollSnapList() || [];
    const total = snaps.length;

    const isSingleSlide = total <= 1;

    // initial setup
    setSnapCount(total);

    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap();

      setSelectedIndex(index);
      setPrevIndex(isSingleSlide ? 0 : (index - 1 + total) % total);
      setNextIndex(isSingleSlide ? 0 : (index + 1) % total);

      const autoplay = emblaApi.plugins()?.autoplay;
      if (autoplay) {
        if (slideTypes[index] === "video") {
          // Defer: Autoplay's own "select" listener calls reset() synchronously,
          // which would restart the timer and override a stop() in this same tick.
          setTimeout(() => autoplay.stop(), 0);
        } else {
          autoplay.play();
        }
      }
    };

    const onScroll = () => {
      setCanScrollNext(!isSingleSlide && emblaApi.canScrollNext());
      setCanScrollPrev(!isSingleSlide && emblaApi.canScrollPrev());
    };

    onSelect();
    onScroll();

    emblaApi.on("select", onSelect);
    emblaApi.on("scroll", onScroll);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("scroll", onScroll);
    };
  }, [emblaApi, width]);

  const value = {
    emblaRef,
    emblaApi,
    emblaOptions,
    canScrollNext,
    canScrollPrev,
    selectedIndex,
    prevIndex,
    nextIndex,
    snapCount,
    useDotButton,
    DotButton,
    scrollSnaps,
    slideDurations,
    setSlideDuration,
  };

  return (
    <div className="slider-ref-wrapper" ref={containerRef}>
      <SliderContext.Provider value={value}>{children}</SliderContext.Provider>
    </div>
  );
};

SliderProvider.propTypes = {
  children: PropTypes.node.isRequired,
  options: PropTypes.shape({
    loop: PropTypes.bool,
    gap: PropTypes.any,
    width: PropTypes.any,
  }),
};

export default SliderProvider;
